/**
 * LiveChat API服务
 * 提供与后端LiveChat配置相关的API调用功能
 */

import type { 
  LiveChatConfig, 
  LiveChatConfigUpdateRequest 
} from '@/types/livechat'

// API基础URL
const API_BASE_URL = '/api'

/**
 * 获取LiveChat配置信息
 * @returns Promise<LiveChatConfig> LiveChat配置
 */
export async function getLiveChatConfig(): Promise<LiveChatConfig> {
  try {
    const response = await fetch(`${API_BASE_URL}/livechat/config`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`获取LiveChat配置失败: ${response.status} ${response.statusText}`)
    }

    const responseData = await response.json()
    console.log('后端API原始响应:', responseData)
    
    // 处理后端返回的数据格式 {success: true, data: {...}}
    const data = responseData.data || responseData
    console.log('解析后的数据:', data)
    
    const config = {
      licenseId: data.license_id || data.licenseId || '',
      groupId: data.group_id || data.groupId || '',
      enabled: data.enabled !== undefined ? data.enabled : false
    }
    
    console.log('最终配置:', config)
    return config
  } catch (error) {
    console.error('获取LiveChat配置失败:', error)
    throw error
  }
}

/**
 * 更新LiveChat配置信息 (管理员功能)
 * @param config LiveChat配置更新数据
 * @returns Promise<void>
 */
export async function updateLiveChatConfig(config: LiveChatConfigUpdateRequest): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/livechat/config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(config)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `更新LiveChat配置失败: ${response.status} ${response.statusText}`)
    }
  } catch (error) {
    console.error('更新LiveChat配置失败:', error)
    throw error
  }
}

/**
 * 检查LiveChat服务可用性
 * @returns Promise<boolean> 服务是否可用
 */
export async function checkLiveChatAvailability(): Promise<boolean> {
  try {
    const config = await getLiveChatConfig()
    return config.enabled && !!config.licenseId
  } catch (error) {
    console.error('检查LiveChat可用性失败:', error)
    return false
  }
}

/**
 * 验证LiveChat License ID格式
 * @param licenseId License ID
 * @returns boolean 格式是否有效
 */
export function validateLicenseId(licenseId: string): boolean {
  // LiveChat License ID通常是数字格式
  return /^\d+$/.test(licenseId.trim())
}

/**
 * 验证LiveChat Group ID格式
 * @param groupId Group ID
 * @returns boolean 格式是否有效
 */
export function validateGroupId(groupId: string): boolean {
  // Group ID可以是数字或字符串，但不能为空
  return groupId.trim().length > 0
}

/**
 * LiveChat配置验证
 * @param config LiveChat配置
 * @returns { valid: boolean, errors: string[] } 验证结果
 */
export function validateLiveChatConfig(config: Partial<LiveChatConfigUpdateRequest>): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (!config.license_id) {
    errors.push('License ID是必填项')
  } else if (!validateLicenseId(config.license_id)) {
    errors.push('License ID格式无效，应为数字')
  }

  if (config.group_id && !validateGroupId(config.group_id)) {
    errors.push('Group ID格式无效')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}